/**
 * خدمة إدارة Push Notifications في التطبيق
 * Push Notifications Service for Frontend
 */

import { PushNotifications, Token, PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { API_BASE_URL } from '../config/api';

/**
 * تهيئة Push Notifications
 * Initialize Push Notifications
 */
export const initializePushNotifications = async (token: string | null): Promise<{ success: boolean; fcmToken?: string }> => {
  // التحقق من أن التطبيق يعمل على منصة native (Android/iOS)
  if (!Capacitor.isNativePlatform()) {
    console.debug('📱 Push Notifications: Not a native platform, skipping FCM setup');
    return { success: false };
  }

  try {
    console.log('📱 Initializing Push Notifications...');

    // طلب إذن الإشعارات
    const permission = await PushNotifications.requestPermissions();
    
    if (permission.receive === 'granted') {
      console.log('✅ Push notification permission granted');
      
      // تسجيل للحصول على Push Notifications
      await PushNotifications.register();
      
      // الاستماع لتسجيل الـ token
      return new Promise((resolve) => {
        PushNotifications.addListener('registration', async (tokenData: Token) => {
          console.log('✅ Push registration success, token:', tokenData.value);
          
          // تسجيل الـ token في الخادم
          if (token) {
            const registered = await registerFCMToken(tokenData.value, token);
            if (registered) {
              console.log('✅ FCM Token registered with server');
            }
          }
          
          resolve({ success: true, fcmToken: tokenData.value });
        });

        // الاستماع لأخطاء التسجيل
        PushNotifications.addListener('registrationError', (error: any) => {
          console.error('❌ Push registration error:', error);
          resolve({ success: false });
        });
      });
    } else {
      console.log('⚠️ Push notification permission denied');
      return { success: false };
    }
  } catch (error) {
    console.error('❌ Error initializing push notifications:', error);
    return { success: false };
  }
};

/**
 * إعداد مستمعي الإشعارات
 * Setup notification listeners
 */
export const setupNotificationListeners = () => {
  if (!Capacitor.isNativePlatform()) {
    return;
  }

  // الاستماع للإشعارات الواردة أثناء فتح التطبيق
  PushNotifications.addListener('pushNotificationReceived', (notification: PushNotificationSchema) => {
    console.log('🔔 Push notification received:', notification);
    
    // يمكن إضافة منطق مخصص هنا لعرض الإشعار في الواجهة
    // مثل إضافة toast أو تحديث العداد
  });

  // الاستماع للإشعارات عند الضغط عليها
  PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
    console.log('🔔 Push notification action performed:', action);
    
    const data = action.notification.data;
    
    // التوجيه بناءً على نوع الإشعار
    if (data.type === 'auction_sold' || data.type === 'auction_won' || data.type === 'auction_unsold') {
      if (data.auctionId) {
        window.location.href = `/auction/${data.auctionId}`;
      }
    } else if (data.type === 'bid_outbid') {
      if (data.auctionId) {
        window.location.href = `/auction/${data.auctionId}`;
      }
    } else if (data.type === 'message') {
      window.location.href = '/messages';
    }
  });
};

/**
 * تسجيل FCM Token في الخادم
 * Register FCM Token with server
 */
export const registerFCMToken = async (fcmToken: string, authToken: string): Promise<boolean> => {
  try {
    const platform = Capacitor.getPlatform(); // 'android', 'ios', or 'web'
    const deviceId = await getDeviceId();

    const response = await fetch(`${API_BASE_URL}/push-notifications/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify({
        token: fcmToken,
        deviceId,
        platform
      })
    });

    if (response.ok) {
      const data = await response.json();
      console.log('✅ FCM Token registered:', data);
      return true;
    } else {
      console.error('❌ Failed to register FCM token:', response.status);
      return false;
    }
  } catch (error) {
    console.error('❌ Error registering FCM token:', error);
    return false;
  }
};

/**
 * إلغاء تسجيل FCM Token من الخادم
 * Unregister FCM Token from server
 */
export const unregisterFCMToken = async (fcmToken: string, authToken: string): Promise<boolean> => {
  try {
    const response = await fetch(`${API_BASE_URL}/push-notifications/unregister`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      },
      body: JSON.stringify({
        token: fcmToken
      })
    });

    if (response.ok) {
      console.log('✅ FCM Token unregistered');
      return true;
    } else {
      console.error('❌ Failed to unregister FCM token');
      return false;
    }
  } catch (error) {
    console.error('❌ Error unregistering FCM token:', error);
    return false;
  }
};

/**
 * اختبار إرسال Push Notification
 * Test sending push notification
 */
export const testPushNotification = async (authToken: string): Promise<boolean> => {
  try {
    const response = await fetch(`${API_BASE_URL}/push-notifications/test`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      }
    });

    if (response.ok) {
      const data = await response.json();
      console.log('✅ Test notification sent:', data);
      return true;
    } else {
      console.error('❌ Failed to send test notification');
      return false;
    }
  } catch (error) {
    console.error('❌ Error sending test notification:', error);
    return false;
  }
};

/**
 * الحصول على Device ID فريد
 * Get unique device ID
 */
const getDeviceId = async (): Promise<string> => {
  try {
    // يمكن استخدام Device plugin من Capacitor للحصول على معرف فريد
    // import { Device } from '@capacitor/device';
    // const info = await Device.getId();
    // return info.identifier;
    
    // في الوقت الحالي، نستخدم معرف مؤقت
    let deviceId = localStorage.getItem('device_id');
    if (!deviceId) {
      deviceId = `device_${Date.now()}_${Math.random().toString(36).substring(7)}`;
      localStorage.setItem('device_id', deviceId);
    }
    return deviceId;
  } catch (error) {
    console.error('Error getting device ID:', error);
    return `device_${Date.now()}`;
  }
};

/**
 * الحصول على FCM Tokens info
 * Get FCM Tokens information
 */
export const getFCMTokensInfo = async (authToken: string): Promise<any> => {
  try {
    const response = await fetch(`${API_BASE_URL}/push-notifications/info`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authToken}`
      }
    });

    if (response.ok) {
      const data = await response.json();
      return data.data;
    } else {
      console.error('❌ Failed to get FCM tokens info');
      return null;
    }
  } catch (error) {
    console.error('❌ Error getting FCM tokens info:', error);
    return null;
  }
};

